import { type DriveFile } from '@/shared/DriveTypes';
import { Add, Check, UnfoldMore } from '@mui/icons-material';
import {
  Avatar,
  CircularProgress,
  FormControl,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import cx from 'classnames';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useRef, useState } from 'react';
import { Link } from 'wouter';
import { colors, darkTheme, lightTheme } from '~/theme.css';
import { cacheFlag, DEV, isBun, LEGACY, publicDocsId } from '~/utils/constants';
import * as DriveAPI from '~/utils/DriveAPI';
import gup from '~/utils/gup';
import { useKeyPress } from '~/utils/hooks/hooks';
import { useTheme } from '~/utils/store';
import { type Profile } from '~/utils/types';
import { colorIsDark, isAShortcut, isInputEvent, isModalEvent, legacyClick, ls } from '~/utils/utils';
import * as styles from './WikiSelector.css';
import { menuItemActive } from './WikiSelector.css';

const { size } = styles;

export const Logo = ({
  alt,
  className,
  src,
  style,
  wiki,
}: {
  alt: string;
  className?: string;
  src: string;
  style?: React.CSSProperties;
  wiki: DriveFile;
}) => {
  const [thumbnailURL, setThumbnailURL] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  return (
    <Avatar
      {...(DEV && {
        imgProps: {
          referrerPolicy: 'no-referrer',
        },
      })}
      {...(className && { className })}
      {...(style && { style })}
      {...(!loaded && {
        style: {
          ...style,
          background: 'rgba(0, 0, 0, 0.08)',
        },
      })}
      slotProps={{
        img: {
          style: {
            // Allows for wide images
            objectFit: 'contain',
          },
        },
      }}
      alt={alt}
      src={thumbnailURL ?? src}
      // Test error handling
      // src=""
      // src="https://example.com/404"
      onLoad={() => setLoaded(true)}
      onError={async () => {
        if (thumbnailURL) return;
        const { logoId } = wiki.properties ?? {};
        if (!logoId) throw new Error('No logoId found');
        setLoading(true);
        const logo = await DriveAPI.get({ id: logoId, fields: 'thumbnailLink,driveId' }).catch(() => undefined);
        if (!logo?.thumbnailLink) {
          setLoading(false);
          throw new Error(`No logo found: ${JSON.stringify({ name: wiki.name, logoId }, null, 2)}`);
        }
        const url = logo.thumbnailLink.replace('s220', `s${size * window.devicePixelRatio}`);
        setThumbnailURL(url);
        setTimeout(() => setLoading(() => false), 50);
        setTimeout(() => setLoaded(() => true), 200);
      }}
    >
      {!loading && (
        <Avatar className={styles.fallbackLogo}>
          <span className={styles.letterFallback}>{wiki.name.charAt(0)}</span>
        </Avatar>
      )}
    </Avatar>
  );
};

const Button = ({
  currentWiki,
  logoId,
  logoURL,
  unicode,
  bgColor,
  fontColor,
  isPublic,
  isLink,
}: {
  currentWiki: DriveFile;
  logoId: string | undefined;
  logoURL: string | undefined;
  unicode: string | undefined;
  fontColor: string | undefined;
  isPublic: boolean | undefined;
  bgColor: string | undefined;
  isLink?: boolean;
}) => {
  const theme = useTheme();
  const color = (theme === 'light' ? fontColor : undefined) ?? '#fff';
  const background =
    theme === 'light' ? (bgColor ? (colorIsDark(bgColor) ? undefined : 'rgba(0, 0, 0, 0.08)') : undefined) : undefined;
  return (
    <ListItemButton className={styles.button} title="Select a wiki (w)">
      {/* Logo */}
      {logoId && !unicode && (
        <Logo
          alt="Wiki logo"
          className={styles.buttonLogo}
          src={
            // If it's public we can save an additional request
            isPublic
              ? `https://lh3.googleusercontent.com/d/${logoId}=w${size * window.devicePixelRatio}`
              : `https://drive.google.com/thumbnail?sz=w${size * window.devicePixelRatio}&id=${logoId}`
          }
          wiki={currentWiki}
          style={{
            background,
            color,
          }}
        />
      )}
      {/* Custom Logo URL */}
      {!logoId && !unicode && logoURL && (
        <Logo
          alt="Wiki logo"
          className={styles.buttonLogo}
          src={logoURL}
          wiki={currentWiki}
          style={{
            background,
            color,
          }}
        />
      )}

      {/* Letter */}
      {!logoId && !unicode && !logoURL && (
        <Avatar
          className={styles.letter}
          style={{
            background,
            color,
          }}
        >
          {currentWiki.name.charAt(0)}
        </Avatar>
      )}
      {/* Emoji */}
      {unicode && (
        <div
          className={styles.unicode}
          style={{ fontSize: 34, lineHeight: 0, width: 34, height: 34, display: 'flex', alignItems: 'center' }}
        >
          {unicode}
        </div>
      )}
      <ListItemText
        slotProps={{
          primary: {
            className: styles.buttonText,
            style: { color },
          },
        }}
        primary={currentWiki.name}
      />
      {!isLink && <UnfoldMore style={{ color }} />}
    </ListItemButton>
  );
};

const WikiSelector = ({
  wiki: currentWiki,
  wikis,
  user,
  isLoading,
}: {
  wiki: DriveFile;
  wikis: DriveFile[] | undefined;
  user: Profile | undefined;
  isLoading?: boolean;
}) => {
  const { logoId, logoURL, unicode, color, fontColor } = currentWiki.properties ?? {};
  const menuState = usePopupState({ variant: 'popover' });
  const anchorRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const isPublic = currentWiki.permissions?.some((permission) => permission.type === 'anyone');
  const hideCreateWikiButton =
    currentWiki.properties?.showCreateWikiButton === 'false' && currentWiki.capabilities?.canEdit === false;
  const wikisFlag = LEGACY ? (gup('wikis') ? `wikis=${gup('wikis')}` : '') : '';

  useKeyPress('w', (e) => {
    if (isInputEvent(e)) return;
    if (isModalEvent(e)) return;
    menuState.setAnchorEl(anchorRef.current);
    menuState.open();
  });

  if (!user && wikis && wikis.length <= 1) {
    return (
      <FormControl className={styles.buttonContainer}>
        <List
          className={styles.buttonLink}
          component={Link}
          aria-label="Wiki selector"
          to={
            LEGACY ? `/app/page/${currentWiki.id}?p=${currentWiki.id}` : `/app/page/${currentWiki.id}/${currentWiki.id}`
          }
          onClick={legacyClick}
        >
          <Button
            currentWiki={currentWiki}
            logoId={logoId}
            logoURL={logoURL}
            unicode={unicode}
            fontColor={theme === 'light' ? fontColor : undefined}
            bgColor={color}
            isPublic={isPublic}
            isLink
          />
        </List>
      </FormControl>
    );
  }

  return (
    <>
      {/* Button */}
      <FormControl className={styles.buttonContainer}>
        <List
          className={styles.list}
          component="nav"
          aria-label="Wiki selector"
          ref={anchorRef}
          {...bindTrigger(menuState)}
        >
          <Button
            currentWiki={currentWiki}
            logoId={logoId}
            logoURL={logoURL}
            unicode={unicode}
            fontColor={fontColor}
            bgColor={color}
            isPublic={isPublic}
          />
        </List>
      </FormControl>

      {/* Menu */}
      <Menu
        {...bindMenu(menuState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        elevation={6}
        transitionDuration={{
          enter: isBun ? 0 : 200,
          exit: 0,
        }}
        className={cx(styles.menu, {
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
        slotProps={{
          paper: {
            style: {
              backgroundColor: 'var(--root-bg-color)',
            },
          },
        }}
      >
        {isLoading && (
          <div className="flex flex-jc" style={{ margin: '1.5rem 0' }}>
            <CircularProgress style={{ color: colors.spinner.default }} thickness={4} size={34} />
          </div>
        )}
        {wikis?.map((wiki) => {
          const id = isAShortcut(wiki) ? wiki.shortcutDetails.targetId : wiki.id;
          const { logoId, unicode, logoURL } = wiki.properties ?? {};
          const isSelected = currentWiki.id === id;
          const isPublic = currentWiki.permissions?.some((permission) => permission.type === 'anyone');
          const { color: bgColor, fontColor } = wiki.properties ?? {};
          const bgColorIsDark = bgColor ? colorIsDark(bgColor) : false;

          // Temp fix until we disable use of iframes
          // Don't show docs.youneedawiki.com in the wiki selector for docs.youneedawiki.com
          if (LEGACY && wiki.id === publicDocsId && gup('parentURL') === 'https://docs.youneedawiki.com') return null;

          const parentFlag = LEGACY ? (gup('p') ? `p=${wiki.id}` : '') : '';
          const flags =
            LEGACY && (parentFlag || wikisFlag) ? `?${[parentFlag, wikisFlag].filter(Boolean).join('&')}` : '';
          const to = LEGACY ? `/app/page/${id}${flags}` : `/app/page/${wiki.id}/${wiki.id}${cacheFlag ? `?cache` : ''}`;

          return (
            <MenuItem
              key={wiki.id}
              onClick={(e) => {
                menuState.close();
                ls.set('current-wiki-id', id);
                legacyClick?.(e);
                if (LEGACY) window.onWikiChange?.(id);
              }}
              component={Link}
              to={to}
              autoFocus={isSelected}
              className={cx(styles.menuItem, {
                [menuItemActive]: isSelected,
              })}
              focusVisibleClassName={styles.menuItemFocusVisible}
            >
              <div className={styles.menuItemText}>
                {wiki.name}
                {isSelected && <Check className={styles.check} />}
              </div>
              {/* Emoji */}
              {unicode && <Avatar className={styles.unicode}>{unicode}</Avatar>}
              {/* Logo */}
              {logoId && !unicode && (
                <Logo
                  className={styles.logo}
                  src={
                    isPublic
                      ? `https://lh3.googleusercontent.com/d/${logoId}=w${size * window.devicePixelRatio}`
                      : `https://drive.google.com/thumbnail?sz=w${size * window.devicePixelRatio}&id=${logoId}`
                  }
                  alt="Wiki logo"
                  wiki={wiki}
                />
              )}
              {/* Legacy: fallback for logoURL */}
              {logoURL && !logoId && !unicode && (
                <Logo className={styles.logo} src={logoURL} alt="Wiki logo" wiki={wiki} />
              )}
              {/* Letter */}
              {!logoId && !unicode && !logoURL && (
                <Avatar
                  className={styles.menuLetter}
                  style={{
                    backgroundColor: bgColor,
                    color: bgColorIsDark ? '#fff' : fontColor === '#FFFFFF' ? undefined : fontColor,
                  }}
                  role="presentation"
                >
                  {wiki.name.charAt(0)}
                </Avatar>
              )}
            </MenuItem>
          );
        })}

        {/* New wiki */}
        {!hideCreateWikiButton && (
          <MenuItem
            onClick={(e) => {
              menuState.close();
              legacyClick?.(e);
              if (window.posthog) posthog.capture('create_wiki_clicked', { location: 'wiki_selector' });
            }}
            component={Link}
            to="/app/create"
            className={styles.menuItem}
            focusVisibleClassName={styles.menuItemFocusVisible}
            style={{
              flexDirection: 'row',
            }}
          >
            <Avatar className={styles.addIcon}>
              <Add style={{ color: 'currentcolor' }} />
            </Avatar>
            {/* Allows searching for "+" */}
            <span
              style={{
                opacity: 0,
                width: 0,
                height: 0,
                position: 'absolute',
                overflow: 'hidden',
              }}
            >
              +
            </span>
            <div className={styles.menuItemText}>Create a new wiki</div>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default WikiSelector;
